import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { resolveDestination, type Access } from './navigation'
import { useSessionStore } from './session'

declare module 'vue-router' {
  interface RouteMeta {
    access: Access
    /** `false` : écran secondaire, sans barre d'onglets sur téléphone. */
    tabs?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  // Page publique : un utilisateur connecté est conduit directement dans l'application.
  {
    path: '/',
    name: 'landing',
    component: () => import('@/features/landing/LandingView.vue'),
    meta: { access: 'guest' },
  },
  {
    path: '/notifications/desabonnement',
    name: 'unsubscribe',
    component: () => import('@/features/notifications/UnsubscribeView.vue'),
    meta: { access: 'public' },
  },
  {
    path: '/conditions',
    name: 'terms',
    component: () => import('@/features/landing/LegalView.vue'),
    meta: { access: 'public' },
  },
  {
    path: '/mentions-legales',
    name: 'legal',
    component: () => import('@/features/landing/LegalView.vue'),
    meta: { access: 'public' },
  },

  // Compte (adresses en français, visibles dans les e-mails et l'historique du navigateur).
  {
    path: '/connexion',
    name: 'sign-in',
    component: () => import('@/features/auth/SignInView.vue'),
    meta: { access: 'guest' },
  },
  {
    path: '/inscription',
    name: 'sign-up',
    component: () => import('@/features/auth/SignUpView.vue'),
    meta: { access: 'guest' },
  },
  {
    path: '/verifier-email',
    name: 'check-email',
    component: () => import('@/features/auth/CheckEmailView.vue'),
    meta: { access: 'guest' },
  },
  {
    path: '/mot-de-passe-oublie',
    name: 'forgot-password',
    component: () => import('@/features/auth/ForgotPasswordView.vue'),
    meta: { access: 'guest' },
  },
  {
    path: '/nouveau-mot-de-passe',
    name: 'new-password',
    component: () => import('@/features/auth/NewPasswordView.vue'),
    meta: { access: 'guest' },
  },
  {
    path: '/auth/retour',
    name: 'auth-callback',
    component: () => import('@/features/auth/AuthCallbackView.vue'),
    meta: { access: 'public' },
  },

  // Configuration initiale (Phase 6).
  {
    path: '/bienvenue',
    name: 'onboarding',
    component: () => import('@/features/onboarding/OnboardingView.vue'),
    meta: { access: 'onboarding' },
  },

  // Application.
  {
    path: '/',
    component: () => import('@/features/shell/AppLayout.vue'),
    meta: { access: 'member' },
    children: [
      {
        path: 'accueil',
        name: 'home',
        component: () => import('@/features/home/HomeView.vue'),
        meta: { access: 'member' },
      },
      {
        path: 'enveloppes',
        name: 'envelopes',
        component: () => import('@/features/envelopes/EnvelopesView.vue'),
        meta: { access: 'member' },
      },
      {
        path: 'activite',
        name: 'activity',
        component: () => import('@/features/activity/ActivityView.vue'),
        meta: { access: 'member' },
      },
      {
        path: 'analyses',
        name: 'analytics',
        component: () => import('@/features/analytics/AnalyticsView.vue'),
        meta: { access: 'member' },
      },
      {
        path: 'repartir',
        name: 'allocate',
        component: () => import('@/features/allocation/AllocationView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'enveloppes/nouvelle',
        name: 'envelope-new',
        component: () => import('@/features/envelopes/EnvelopeFormView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'enveloppes/:id',
        name: 'envelope',
        component: () => import('@/features/envelopes/EnvelopeView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'enveloppes/:id/modifier',
        name: 'envelope-edit',
        component: () => import('@/features/envelopes/EnvelopeFormView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'administration',
        name: 'admin',
        component: () => import('@/features/admin/AdminView.vue'),
        meta: { access: 'admin', tabs: false },
      },
      {
        path: 'administration/campagnes',
        name: 'admin-campaigns',
        component: () => import('@/features/admin/mailing/CampaignsView.vue'),
        meta: { access: 'admin', tabs: false },
      },
      {
        path: 'administration/campagnes/:id',
        name: 'admin-campaign',
        component: () => import('@/features/admin/mailing/CampaignEditView.vue'),
        meta: { access: 'admin', tabs: false },
      },
      {
        path: 'administration/modeles',
        name: 'admin-templates',
        component: () => import('@/features/admin/mailing/TemplatesView.vue'),
        meta: { access: 'admin', tabs: false },
      },
      {
        path: 'administration/modeles/:id',
        name: 'admin-template',
        component: () => import('@/features/admin/mailing/TemplateEditView.vue'),
        meta: { access: 'admin', tabs: false },
      },
      {
        path: 'administration/parcours',
        name: 'admin-automations',
        component: () => import('@/features/admin/mailing/AutomationsView.vue'),
        meta: { access: 'admin', tabs: false },
      },
      {
        path: 'reglages',
        name: 'settings',
        component: () => import('@/features/settings/SettingsView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'bilan/:period(\\d{4}-\\d{2})',
        name: 'review',
        component: () => import('@/features/review/MonthReviewView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'guide',
        name: 'guide',
        component: () => import('@/features/help/HelpView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'objectifs',
        name: 'goals',
        component: () => import('@/features/goals/GoalsView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'reglages/securite',
        name: 'security',
        component: () => import('@/features/lock/SecurityView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'reglages/notifications',
        name: 'notifications',
        component: () => import('@/features/notifications/NotificationsView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'reglages/profil',
        name: 'profile',
        component: () => import('@/features/settings/ProfileView.vue'),
        meta: { access: 'member', tabs: false },
      },
      {
        path: 'reglages/echeances',
        name: 'recurring',
        component: () => import('@/features/recurring/RecurringView.vue'),
        meta: { access: 'member', tabs: false },
      },
    ],
  },

  // Vitrine du design system : développement uniquement, absente du build de production.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/_kit/:section?',
          name: 'kit',
          component: () => import('@/features/kit/KitView.vue'),
          meta: { access: 'public' as const },
        },
      ]
    : []),

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/foundation/NotFoundView.vue'),
    meta: { access: 'public' },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Ancres de la page publique (#application…) : défilement doux, sous l'en-tête fixe.
  scrollBehavior: (to, _from, savedPosition) =>
    savedPosition ?? (to.hash ? { el: to.hash, top: 80, behavior: 'smooth' } : { top: 0 }),
})

router.beforeEach(async (to) => {
  const session = useSessionStore()
  await session.start()
  const destination = resolveDestination(
    {
      access: to.meta.access,
      name: typeof to.name === 'string' ? to.name : '',
      fullPath: to.fullPath,
    },
    session.facts,
  )
  if (destination.kind === 'allow') return true
  return { name: destination.name, ...(destination.query ? { query: destination.query } : {}) }
})
